import './SideNav.scss';
import { AppBar, AppBarSection, Drawer, DrawerContent } from '@progress/kendo-react-layout';
import { useSelector } from 'react-redux';
import { useResize } from '../../hooks/resize';
import { selectProjectSelectorVisible, selectSidebarOpen } from '../../store/slices/layout.slice';
import { useSidebarOpen } from '../../hooks/layout';
import FilterBar from './filters/FilterBar';
import { PageTabs } from './PageTabs';
import { selectIsFlyoutVisbile } from '../../store/slices/flyout.slice';
import { FlyoutLayout } from '../flyouts/FlyoutLayout';
import PowerpointExportWizardFlyout from '../flyouts/PowerpointExportWizard';
import { selectIsPptFlyoutVisible } from '../../store/slices/export-ppt.slice';
import ActiveSection from './ActiveSection';
import TopNav from './TopNav';
import { MyFilesLink } from './MyFilesLink';
import { ProjectSelector } from './ProjectSelector';
import SideNavContainer from './SideNav/SideNavContainer';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '../../../ErrorFallback';
import { MobileSideNav } from '../mobile/menu/MobileSideNav';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { PROFILE_CAPTURE_ROUTES } from 'routes/routes.const';
import TabletSideNav from '../mobile/menu/TabletSideNav';
import { RootState } from '../../store';
import { selectFiltersVisible } from '../../store/slices/filter-builder.slice';
import { DATASET_INITIAL_ID } from '../../constants';
import ApiDropdown from '../ApiDropdown';
import AiBanner from '../aiBanner/AiBanner';

interface SideNavProps {
  drawerRef: React.RefObject<any>;
  children: React.ReactNode;
}

export default function SideNav({ drawerRef, children }: Readonly<SideNavProps>) {
  const isSidebarOpen = useSelector(selectSidebarOpen);
  const projectSelectorVisible = useSelector(selectProjectSelectorVisible);
  const isFlyoutVisible = useSelector(selectIsFlyoutVisbile);
  const isPptFlyoutVisible = useSelector(selectIsPptFlyoutVisible);
  const { isMobile, isTabletOrMobile } = useResize();
  const { compareUrl } = useCompareUrlWithProjectId();
  const isProfileCapture = compareUrl(PROFILE_CAPTURE_ROUTES.root, { includes: true });
  const isFilterSideOpen = useSelector((state: RootState) =>
    selectFiltersVisible(state, DATASET_INITIAL_ID)
  );

  useSidebarOpen(drawerRef);

  return (
    <Drawer
      ref={drawerRef}
      animation={true}
      expanded={isSidebarOpen}
      position="start"
      className="wrapall"
    >
      {!isTabletOrMobile && <SideNavContainer />}
      <DrawerContent className={isFilterSideOpen ? 'overflow-hidden' : ''}>
        <AppBar themeColor="dark" className="side-nav-app-bar">
          {!isMobile && isTabletOrMobile && <TabletSideNav />}
          <AppBarSection>{!isTabletOrMobile && <ActiveSection />}</AppBarSection>
          {!isMobile && (
            <>
              <AppBarSection className="top-nav-section">
                <TopNav />
              </AppBarSection>
            </>
          )}
          <AppBarSection>
            <ApiDropdown />
            {!isMobile && !isProfileCapture && <MyFilesLink />}
            {projectSelectorVisible && !isMobile && <ProjectSelector />}
            {isMobile && <MobileSideNav />}
          </AppBarSection>
        </AppBar>
        <PageTabs>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <div className="k-drawer-content-scroll">
              <div className="main-content">
                <div id="content-container" className="scroll">
                  {children}
                </div>
              </div>

              <FilterBar />

              {isFlyoutVisible && <FlyoutLayout />}
              {isPptFlyoutVisible && <PowerpointExportWizardFlyout />}
            </div>
          </ErrorBoundary>
        </PageTabs>
        <AiBanner />
      </DrawerContent>
    </Drawer>
  );
}
